// src/services/whsoAlertService.ts
// ─────────────────────────────────────────────────────────────────
// WHSO Alert Service — Phase 1 (Mock)
// At beta: replace bodies with fetch('/api/whso-alerts/...')
// ─────────────────────────────────────────────────────────────────

import type {
  WHSOAlert, WHSOAlertType, WHSOAlertSeverity, WHSOAlertStatus,
} from '../types/mobileCrewTypes'
import { addCrewNotification } from '../utils/mockDataNotificationBus'

// ── Module-level alert store ────────────────────────────────────
let _alertStore: WHSOAlert[] = []
let _alertSeq = 0

function nextAlertId(): string {
  return `ALERT-${String(++_alertSeq).padStart(4, '0')}`
}

// ── Seed alerts ─────────────────────────────────────────────────
const SEED_ALERTS: Omit<WHSOAlert, 'id'>[] = [
  {
    alertType:              'WHS_HIGH_RISK',
    severity:               'HIGH',
    source:                 'MANUAL',
    status:                 'SENT',
    linkedWHSRecordId:      null,
    linkedWHSModule:        null,
    affectedJobIds:         ['j2'],
    affectedJobNumbers:     ['TP-2026-002'],
    affectedCrewIds:        ['e7'],
    heading:                'High voltage work — additional authorisation required',
    body:                   'All HV work on TP-2026-002 requires written Energex authorisation before energisation. Confirm you have sighted the Network Access Permit before switching.',
    requiredAction:         'Confirm you have sighted the Network Access Permit before commencing energisation work.',
    attachmentUrl:          null,
    acknowledgementRequired:true,
    acknowledgements:       [{ employeeId: 'e7', employeeName: 'Mike Torres', acknowledgedAt: '2026-07-11T07:30:00Z' }],
    acknowledgedCount:      1,
    totalRecipients:        1,
    createdAt:              '2026-07-11T07:00:00Z',
    createdById:            'u4',
    createdByName:          'Sarah Lee',
    sentAt:                 '2026-07-11T07:01:00Z',
    expiresAt:              null,
    archivedAt:             null,
  },
  {
    alertType:              'WEATHER',
    severity:               'MEDIUM',
    source:                 'AUTO_WEATHER',
    status:                 'SENT',
    linkedWHSRecordId:      null,
    linkedWHSModule:        null,
    affectedJobIds:         ['j3'],
    affectedJobNumbers:     ['TP-2026-003'],
    affectedCrewIds:        ['e4'],
    heading:                'BOM Flood Watch — Gold Coast Hinterland',
    body:                   'Bureau of Meteorology has issued a Flood Watch for the Gold Coast Hinterland region. Monitor site conditions and be prepared to suspend works.',
    requiredAction:         'Check site drainage and report any water ingress to PM before commencing today.',
    attachmentUrl:          null,
    acknowledgementRequired:false,
    acknowledgements:       [],
    acknowledgedCount:      0,
    totalRecipients:        1,
    createdAt:              '2026-07-11T06:00:00Z',
    createdById:            'system',
    createdByName:          'BOM Auto-alert',
    sentAt:                 '2026-07-11T06:00:00Z',
    expiresAt:              '2026-07-12T23:59:00Z',
    archivedAt:             null,
  },
  // DRAFT — auto-generated from a SWMS update, awaiting PM review before send
  {
    alertType:              'SWMS_UPDATE',
    severity:               'CRITICAL',
    source:                 'AUTO_SWMS',
    status:                 'DRAFT',
    linkedWHSRecordId:      'swms-004',
    linkedWHSModule:        'WHS → SWMS',
    affectedJobIds:         ['j1'],
    affectedJobNumbers:     ['TP-2026-001'],
    affectedCrewIds:        ['e1', 'e5'],
    heading:                'SWMS Rev 3 — Underground Cable Installation',
    body:                   'SWMS-2026-004 has been updated to Rev 3. All named workers must re-read and re-sign before commencing work.',
    requiredAction:         'Open SWMS-2026-004 Rev 3, read all changes, and sign off in the mobile app before starting today.',
    attachmentUrl:          null,
    acknowledgementRequired:true,
    acknowledgements:       [],
    acknowledgedCount:      0,
    totalRecipients:        2,
    createdAt:              '2026-07-11T05:50:00Z',
    createdById:            'system',
    createdByName:          'SWMS Auto-alert',
    sentAt:                 null,
    expiresAt:              null,
    archivedAt:             null,
  },
]

// Load seeds
_alertStore = SEED_ALERTS.map(s => ({ ...s, id: nextAlertId() }))

// ── Service functions ────────────────────────────────────────────

export interface CreateAlertInput {
  alertType: WHSOAlertType
  severity: WHSOAlertSeverity
  affectedJobIds: string[]
  affectedJobNumbers: string[]
  affectedCrewIds: string[]
  heading: string
  body: string
  requiredAction?: string
  attachmentUrl?: string
  acknowledgementRequired: boolean
  expiresAt?: string
  linkedWHSRecordId?: string
  linkedWHSModule?: string
  createdById: string
  createdByName: string
  source?: WHSOAlertStatus | 'AUTO_INCIDENT' | 'AUTO_SWMS' | 'AUTO_PLANT' | 'AUTO_INCIDENT' | 'AUTO_SWMS' | 'AUTO_PLANT'
}

/** Get all alerts, optionally filtered by job. */
export async function getAlerts(jobId?: string): Promise<WHSOAlert[]> {
  // Phase 6: GET /api/whso-alerts?jobId=...
  const results = jobId
    ? _alertStore.filter(a => a.affectedJobIds.includes(jobId))
    : [..._alertStore]
  return Promise.resolve(results.slice().reverse())
}

/** Get DRAFT alerts awaiting PM review (auto-generated from platform events). */
export async function getDraftAlerts(): Promise<WHSOAlert[]> {
  // Phase 6: GET /api/whso-alerts?status=DRAFT
  return Promise.resolve(_alertStore.filter(a => a.status === 'DRAFT'))
}

/** Create a new alert (DRAFT status). PM reviews and calls sendAlert() to dispatch. */
export async function createAlert(input: CreateAlertInput): Promise<WHSOAlert> {
  // Phase 6: POST /api/whso-alerts
  const alert: WHSOAlert = {
    id:                     nextAlertId(),
    alertType:              input.alertType,
    severity:               input.severity,
    source:                 'MANUAL',
    status:                 'DRAFT',
    linkedWHSRecordId:      input.linkedWHSRecordId ?? null,
    linkedWHSModule:        input.linkedWHSModule ?? null,
    affectedJobIds:         input.affectedJobIds,
    affectedJobNumbers:     input.affectedJobNumbers,
    affectedCrewIds:        input.affectedCrewIds,
    heading:                input.heading,
    body:                   input.body,
    requiredAction:         input.requiredAction ?? null,
    attachmentUrl:          input.attachmentUrl ?? null,
    acknowledgementRequired:input.acknowledgementRequired,
    acknowledgements:       [],
    acknowledgedCount:      0,
    totalRecipients:        input.affectedCrewIds.length,
    createdAt:              new Date().toISOString(),
    createdById:            input.createdById,
    createdByName:          input.createdByName,
    sentAt:                 null,
    expiresAt:              input.expiresAt ?? null,
    archivedAt:             null,
  }
  _alertStore.push(alert)
  return alert
}

/** Send a DRAFT alert to crew via the notification bus. */
export async function sendAlert(alertId: string): Promise<WHSOAlert | null> {
  // Phase 6: POST /api/whso-alerts/:id/send
  const idx = _alertStore.findIndex(a => a.id === alertId)
  if (idx < 0) return null

  const alert = { ..._alertStore[idx], status: 'SENT' as const, sentAt: new Date().toISOString() }
  _alertStore[idx] = alert

  // Dispatch via notification bus (simulated in Phase 1)
  addCrewNotification({
    type:          'CUSTOM_WHSO_ALERT',
    priority:      alert.severity,
    employeeIds:   alert.affectedCrewIds,
    jobIds:        alert.affectedJobIds,
    heading:       alert.heading,
    body:          alert.body,
    requiredAction:alert.requiredAction ?? undefined,
    attachmentUrl: alert.attachmentUrl ?? undefined,
    sourceModule:  'whsoAlertService',
    sourceRecordId: alert.id,
    requiresAck:   alert.acknowledgementRequired,
    payload:       { alertId: alert.id, alertType: alert.alertType },
  })

  return alert
}

/** Record acknowledgement of an alert by a crew member. */
export async function acknowledgeAlert(
  alertId: string,
  employeeId: string,
  employeeName: string,
): Promise<boolean> {
  // Phase 6: PATCH /api/whso-alerts/:id/acknowledge
  const alert = _alertStore.find(a => a.id === alertId)
  if (!alert) return false

  if (!alert.acknowledgements.find(a => a.employeeId === employeeId)) {
    alert.acknowledgements.push({ employeeId, employeeName, acknowledgedAt: new Date().toISOString() })
    alert.acknowledgedCount = alert.acknowledgements.length
  }
  return true
}

/** Resend an already-sent alert to unacknowledged crew only. */
export async function resendToUnacknowledged(alertId: string): Promise<boolean> {
  // Phase 6: POST /api/whso-alerts/:id/resend
  const alert = _alertStore.find(a => a.id === alertId)
  if (!alert) return false

  const unacked = alert.affectedCrewIds.filter(
    id => !alert.acknowledgements.find(a => a.employeeId === id)
  )
  if (!unacked.length) return false

  addCrewNotification({
    type:          'CUSTOM_WHSO_ALERT',
    priority:      'HIGH',
    employeeIds:   unacked,
    jobIds:        alert.affectedJobIds,
    heading:       `REMINDER: ${alert.heading}`,
    body:          alert.body,
    requiredAction: alert.requiredAction ?? undefined,
    sourceModule:  'whsoAlertService',
    sourceRecordId: alert.id,
    requiresAck:   true,
    payload:       { alertId: alert.id, isReminder: true },
  })
  return true
}
